import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type { Industry, IndustryResponse, IndustryLabels, EffectiveSettings } from '@/lib/types';

const FIVE_MINUTES = 1000 * 60 * 5;

const INDUSTRY_LABELS: Record<Industry, IndustryLabels> = {
  field_ops: {
    location: 'Job Site',
    zone: 'Area',
    spot: 'Toolbox',
    client: 'Client',
  },
  facility_ops: {
    location: 'Zone',
    zone: 'Department',
    spot: 'Locker',
    client: 'Department',
  },
  training_ops: {
    location: 'Training Venue',
    zone: 'Training Area',
    spot: 'Equipment Store',
    client: 'Client',
  },
  consultant: {
    location: 'Client Site',
    zone: 'Department',
    spot: 'Area',
    client: 'Client',
  },
  hybrid: {
    location: 'Location',
    zone: 'Zone',
    spot: 'Spot',
    client: 'Client',
  },
};

export { INDUSTRY_LABELS };

type FieldRelevance = EffectiveSettings['field_relevance'];

const FIELD_RELEVANCE_DEFAULTS: Record<Industry, FieldRelevance> = {
  field_ops: { uses_vehicles: true, requires_address: true, track_installed_gear: false },
  facility_ops: { uses_vehicles: false, requires_address: false, track_installed_gear: true },
  training_ops: { uses_vehicles: false, requires_address: true, track_installed_gear: true },
  consultant: { uses_vehicles: false, requires_address: true, track_installed_gear: false },
  hybrid: { uses_vehicles: true, requires_address: true, track_installed_gear: true },
};

export { FIELD_RELEVANCE_DEFAULTS };

export function useIndustry() {
  const query = useQuery({
    queryKey: queryKeys.settings.industry(),
    queryFn: async (): Promise<IndustryResponse> => {
      const response = await apiClient.get<IndustryResponse>('/api/v1/org-settings/industry/');
      return response.data;
    },
    staleTime: FIVE_MINUTES,
  });

  const rawIndustry = query.data?.industry;
  const VALID_INDUSTRIES: Industry[] = ['field_ops', 'facility_ops', 'training_ops', 'consultant', 'hybrid'];
  const industry: Industry = rawIndustry && VALID_INDUSTRIES.includes(rawIndustry) ? rawIndustry : 'field_ops';
  const labels = INDUSTRY_LABELS[industry];
  const fieldRelevance = FIELD_RELEVANCE_DEFAULTS[industry];

  return { ...query, industry, labels, fieldRelevance };
}

export function useSetIndustry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (industry: Industry): Promise<IndustryResponse> => {
      const response = await apiClient.post<IndustryResponse>(
        '/api/v1/org-settings/industry/',
        { industry }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.industry() });
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.organization() });
    },
  });
}
